import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Mic,
  Volume2,
  Wifi,
  Globe,
  CheckCircle,
  XCircle,
  Loader,
  AlertTriangle,
  RefreshCw,
  ChevronDown,
  Play,
  X,
  Monitor,
} from "lucide-react";
import apiClient from "../../utils/apiClient";

// ── Class strings ─────────────────────────────────────────────────────────
// Converted to Tailwind 2026-08-25 (Phase 5); PreMeetingCheck.scss deleted with it.
//
// 104 colour literals — the densest sheet on the candidate list — and a 60-line
// `[data-theme="dark"]` block that existed only to re-mute the four status washes
// the light half had hardcoded. Both halves are one token pair now:
//
//     .pmc__item--pass   #f0fdf4 / border #bbf7d0        (light, hardcoded)
//     [dark] .pmc__item--pass   rgba(34,197,94,.08 / .3) (dark, hand-muted)
//     →  tw-bg-success/[0.08] tw-border-success/30       (both)
//
// The row wash and the icon pill are deliberately two different steps of the same
// hue — `/[0.08]` and `-soft` — because the sheet had them one step apart
// (green-50 behind green-100) and collapsing them would make the pill vanish into
// the row it sits on.
//
// ⚠️ TWO DYNAMIC CLASS NAMES, both fatal under Tailwind. `pmc__item--${status}`
// and `pmc__icon--${status}` were assembled at runtime; the content scanner reads
// source TEXT, so those classes would never be generated. ITEM_TONE / PILL_TONE
// are keyed by the same STATUS values, which is the only shape that works.
const OVERLAY =
  "tw-fixed tw-inset-0 tw-z-critical tw-flex tw-items-center tw-justify-center tw-p-4 " +
  "tw-bg-[var(--overlay-bg)] tw-backdrop-blur-[4px]";

// --popover-shadow is 0.18 in light and 0.55 in dark, which is the sheet's two
// hardcoded alphas exactly — so the dark override for it is gone, not ported.
const DIALOG =
  "tw-relative tw-max-h-[90vh] tw-w-full tw-max-w-[560px] tw-animate-dialog-in " +
  "tw-overflow-y-auto tw-rounded-[14px] tw-border tw-border-solid tw-border-line " +
  "tw-bg-surface tw-shadow-[0_12px_40px_var(--popover-shadow)]";

const BTN =
  "tw-inline-flex tw-cursor-pointer tw-items-center tw-gap-[5px] tw-rounded-sm " +
  "tw-border tw-border-solid tw-border-transparent tw-px-4 tw-py-[7px] " +
  "tw-text-[0.82rem] tw-font-semibold " +
  "tw-transition-[background,border-color,opacity] tw-duration-150 " +
  "disabled:tw-cursor-not-allowed disabled:tw-opacity-[0.55]";

const BTN_PRIMARY =
  `${BTN} tw-border-primary-solid tw-bg-primary-solid tw-text-primary-solid-fg ` +
  "enabled:hover:tw-bg-[color-mix(in_srgb,hsl(var(--primary-solid))_88%,black)]";

const BTN_CANCEL =
  `${BTN} tw-border-line tw-bg-transparent tw-text-content ` +
  "enabled:hover:tw-bg-surface-sunken";

const BTN_GHOST =
  "tw-me-auto tw-inline-flex tw-cursor-pointer tw-items-center tw-gap-[5px] " +
  "tw-rounded-sm tw-border-none tw-bg-transparent tw-px-2.5 tw-py-[7px] " +
  "tw-text-[0.82rem] tw-font-semibold tw-text-content-muted " +
  "tw-transition-[color,opacity] tw-duration-150 enabled:hover:tw-text-content " +
  "disabled:tw-cursor-not-allowed disabled:tw-opacity-[0.55]";

const ITEM =
  "tw-flex tw-items-center tw-gap-3 tw-rounded-sm tw-border tw-border-solid " +
  "tw-px-[0.9rem] tw-py-[0.7rem] tw-transition-[border-color,background] tw-duration-200";

const ITEM_TONE = {
  pass: "tw-border-success/30 tw-bg-success/[0.08]",
  fail: "tw-border-danger/30 tw-bg-danger/[0.08]",
  checking: "tw-border-info/30 tw-bg-info/[0.08]",
  idle: "tw-border-line tw-bg-surface-alt",
};

const PILL =
  "tw-flex tw-h-[34px] tw-w-[34px] tw-shrink-0 tw-items-center tw-justify-center tw-rounded-sm";

const PILL_TONE = {
  pass: "tw-bg-success-soft tw-text-success",
  fail: "tw-bg-danger-soft tw-text-danger",
  checking: "tw-bg-info-soft tw-text-info",
  idle: "tw-bg-surface-sunken tw-text-content-muted",
};

const DETAIL = "tw-m-0 tw-text-xs tw-leading-[1.4] tw-text-content-muted";

const SELECT =
  "tw-w-full tw-appearance-none tw-text-ellipsis tw-rounded-[6px] tw-border " +
  "tw-border-solid tw-border-[var(--input-border)] tw-bg-[var(--input-bg)] " +
  "tw-py-[5px] tw-pe-6 tw-ps-2 tw-text-[0.72rem] tw-text-content tw-outline-none " +
  "tw-transition-[border-color] tw-duration-150 " +
  "focus:tw-border-primary focus:tw-ring-2 focus:tw-ring-primary/[0.15]";

const TEST_BTN =
  "tw-mt-[5px] tw-inline-flex tw-cursor-pointer tw-items-center tw-gap-[3px] " +
  "tw-rounded-[5px] tw-border tw-border-solid tw-border-[var(--input-border)] " +
  "tw-bg-transparent tw-px-2 tw-py-[3px] tw-text-[0.7rem] tw-text-content-muted " +
  "tw-transition-colors tw-duration-150 hover:tw-bg-surface-sunken";

// The stale-session block had NO dark override at all — pale pink on a near-black
// card. The tokens give it one for free.
const STALE_ROW =
  "tw-flex tw-items-center tw-justify-between tw-gap-2 tw-rounded-[5px] tw-border " +
  "tw-border-solid tw-border-danger/30 tw-bg-danger/[0.08] tw-px-2 tw-py-[5px] " +
  "tw-text-[0.72rem]";

const STALE_END_BTN =
  "tw-inline-flex tw-shrink-0 tw-cursor-pointer tw-items-center tw-gap-[3px] " +
  "tw-rounded-[4px] tw-border-none tw-bg-danger tw-px-2.5 tw-py-[3px] " +
  "tw-text-[0.68rem] tw-font-semibold tw-text-on-status tw-transition-colors " +
  "tw-duration-150 enabled:hover:tw-bg-[color-mix(in_srgb,hsl(var(--danger))_85%,black)] " +
  "disabled:tw-cursor-not-allowed disabled:tw-opacity-60";

// --elevate-2 rather than --border-primary: the sheet used the border token in
// light and then had to override it to rgba(255,255,255,.1) in dark, because a
// #1f2330 track is invisible on a #14171f card. --elevate-2 IS that inversion
// (rgba(0,0,0,.08) light, rgba(255,255,255,.09) dark) and needs no override.
const TRACK = "tw-bg-[var(--elevate-2)]";

const TIP =
  "tw-mx-5 tw-flex tw-items-start tw-gap-[0.4rem] tw-rounded-[7px] tw-border " +
  "tw-border-solid tw-border-warning/30 tw-bg-warning/10 tw-px-3 tw-py-[0.6rem] " +
  "tw-text-xs tw-text-warning-fg [&>svg]:tw-mt-px [&>svg]:tw-shrink-0";

const TOPIC_INPUT =
  "tw-w-full tw-rounded-sm tw-border-[1.5px] tw-border-solid tw-bg-surface-alt " +
  "tw-px-3 tw-py-2 tw-text-sm tw-text-content tw-outline-none " +
  "tw-transition-[border-color,box-shadow] tw-duration-150 " +
  "placeholder:tw-text-content-subtle " +
  "focus:tw-border-primary focus:tw-bg-surface-raised focus:tw-ring-[3px] focus:tw-ring-primary/[0.15]";

/* ──────────────── check statuses ──────────────── */
const STATUS = { IDLE: "idle", CHECKING: "checking", PASS: "pass", FAIL: "fail" };

/* ──────────────── browser compatibility ──────────────── */
const checkBrowser = () => {
  const ua = navigator.userAgent;
  const isChrome = /Chrome/.test(ua) && !/Edg|OPR/.test(ua);
  const isEdge = /Edg/.test(ua);
  const isFirefox = /Firefox/.test(ua);
  const isSafari = /Safari/.test(ua) && !/Chrome/.test(ua);
  const supported = isChrome || isEdge || isFirefox;
  let name = "Unknown";
  if (isChrome) name = "Google Chrome";
  else if (isEdge) name = "Microsoft Edge";
  else if (isFirefox) name = "Mozilla Firefox";
  else if (isSafari) name = "Safari";
  return { supported, name };
};

/* ──────────────── internet speed test ──────────────── */
const measureSpeed = () =>
  new Promise((resolve) => {
    const imageUrl = "https://upload.wikimedia.org/wikipedia/commons/3/3a/Cat03.jpg";
    const fileSizeBytes = 741_000;
    const startTime = performance.now();
    fetch(imageUrl, { cache: "no-store", mode: "cors" })
      .then((r) => r.blob())
      .then((blob) => {
        const elapsed = (performance.now() - startTime) / 1000;
        const bytes = blob.size || fileSizeBytes;
        const mbps = ((bytes * 8) / elapsed / 1_000_000).toFixed(1);
        resolve(Number.parseFloat(mbps));
      })
      .catch(() => {
        if (navigator.connection?.downlink) {
          resolve(navigator.connection.downlink);
        }
        resolve(0);
      });
  });

/* ──────────────── mic level meter (0-10) ──────────────── */
const MIC_BARS = 10;

const MicLevel = ({ level }) => (
  <div className="tw-mt-[3px] tw-flex tw-items-center tw-gap-0.5">
    {Array.from({ length: MIC_BARS }, (_, i) => (
      <div
        key={i}
        className={`tw-h-2.5 tw-w-[3px] tw-rounded-[1.5px] tw-transition-colors tw-duration-100 ${
          i < level ? "tw-bg-success" : TRACK
        }`}
      />
    ))}
  </div>
);

/* ──────────────── status icon helper ──────────────── */
// The tick and the cross used to carry `color="#16a34a"` / `color="#dc2626"` as
// PROPS, which is why the jscolor check counts them — lucide falls back to
// currentColor, so a text utility does the same job and follows the theme.
const StatusIcon = ({ status }) => {
  if (status === STATUS.PASS)
    return <CheckCircle size={16} className="tw-shrink-0 tw-text-success" />;
  if (status === STATUS.FAIL)
    return <XCircle size={16} className="tw-shrink-0 tw-text-danger" />;
  if (status === STATUS.CHECKING)
    return <Loader size={16} className="tw-inline-block tw-shrink-0 tw-animate-spin" />;
  return null;
};

/* ──────────────────────────────────────────────────
   PreMeetingCheck — modal-based system readiness check
   Props:
     show        — visibility flag
     role        — "instructor" | "student"
     batchName   — optional, for display
     onReady     — callback when user clicks "Join / Start"
     onCancel    — callback when user closes the modal
     confirmText — button label (default: "Join Class")
────────────────────────────────────────────────── */
const PreMeetingCheck = ({ show, role = "student", batchName, batchId, onReady, onCancel, confirmText = "Join Class", defaultTopic = "" }) => {
  const [mic, setMic] = useState({ status: STATUS.IDLE, detail: "" });
  const [speaker, setSpeaker] = useState({ status: STATUS.IDLE, detail: "" });
  const [internet, setInternet] = useState({ status: STATUS.IDLE, detail: "", mbps: 0 });
  const [browser, setBrowser] = useState({ status: STATUS.IDLE, detail: "" });
  const [zoomHost, setZoomHost] = useState({ status: STATUS.IDLE, detail: "" });
  const [staleHosts, setStaleHosts] = useState([]);
  const [endingStale, setEndingStale] = useState(false);
  const [sessionTopic, setSessionTopic] = useState("");

  const [micLevel, setMicLevel] = useState(0);
  const [allDone, setAllDone] = useState(false);
  const [hasFailure, setHasFailure] = useState(false);
  const audioCtxRef = useRef(null);
  const micStreamRef = useRef(null);
  const analyserTimerRef = useRef(null);
  const analyserRef = useRef(null);

  // Device lists & selection
  const [microphones, setMicrophones] = useState([]);
  const [speakers, setSpeakers] = useState([]);
  const [selectedMic, setSelectedMic] = useState("");
  const [selectedSpeaker, setSelectedSpeaker] = useState("");
  const [micLiveActive, setMicLiveActive] = useState(false);

  // Enumerate devices
  const enumerateDevices = useCallback(async () => {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const mics = devices.filter((d) => d.kind === "audioinput");
      const spks = devices.filter((d) => d.kind === "audiooutput");
      setMicrophones(mics);
      setSpeakers(spks);
      if (!selectedMic && mics.length) setSelectedMic(mics[0].deviceId);
      if (!selectedSpeaker && spks.length) setSelectedSpeaker(spks[0].deviceId);
    } catch {
      // permissions not yet granted — will retry after mic check
    }
  }, [selectedMic, selectedSpeaker]);

  useEffect(() => {
    if (!show) return;
    enumerateDevices();
    navigator.mediaDevices.addEventListener?.("devicechange", enumerateDevices);
    return () => {
      navigator.mediaDevices.removeEventListener?.("devicechange", enumerateDevices);
    };
  }, [enumerateDevices, show]);

  // Cleanup on unmount or hide
  const cleanup = useCallback(() => {
    if (micStreamRef.current) {
      micStreamRef.current.getTracks().forEach((t) => t.stop());
      micStreamRef.current = null;
    }
    if (analyserTimerRef.current) clearInterval(analyserTimerRef.current);
    if (audioCtxRef.current) audioCtxRef.current.close().catch(() => {});
    setMicLiveActive(false);
    setMicLevel(0);
  }, []);

  useEffect(() => {
    return cleanup;
  }, [cleanup]);

  // Reset & run checks when modal opens
  useEffect(() => {
    if (show) {
      setSessionTopic(defaultTopic || "");
      runAllChecksRef.current();
    } else {
      cleanup();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  // Close on Escape
  useEffect(() => {
    if (!show) return;
    const handleKey = (e) => { if (e.key === "Escape") onCancel?.(); };
    document.addEventListener("keydown", handleKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", handleKey);
      document.body.style.overflow = "";
    };
  }, [show, onCancel]);

  /* ──── Start live mic level monitor ──── */
  const startLiveMicMonitor = useCallback(async (deviceId) => {
    if (micStreamRef.current) {
      micStreamRef.current.getTracks().forEach((t) => t.stop());
    }
    if (analyserTimerRef.current) clearInterval(analyserTimerRef.current);
    if (audioCtxRef.current) audioCtxRef.current.close().catch(() => {});

    try {
      const constraints = deviceId
        ? { audio: { deviceId: { exact: deviceId } } }
        : { audio: true };
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      micStreamRef.current = stream;
      const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
      audioCtxRef.current = audioCtx;
      const source = audioCtx.createMediaStreamSource(stream);
      const analyser = audioCtx.createAnalyser();
      analyser.fftSize = 256;
      source.connect(analyser);
      analyserRef.current = analyser;
      const data = new Uint8Array(analyser.frequencyBinCount);

      analyserTimerRef.current = setInterval(() => {
        analyser.getByteFrequencyData(data);
        const avg = data.reduce((a, b) => a + b, 0) / data.length;
        const level = Math.min(MIC_BARS, Math.round((avg / 128) * MIC_BARS));
        setMicLevel(level);
      }, 100);
      setMicLiveActive(true);
    } catch {
      setMicLiveActive(false);
    }
  }, []);

  /* ──── 1. Browser check ──── */
  const runBrowserCheck = useCallback(() => {
    setBrowser({ status: STATUS.CHECKING, detail: "Checking…" });
    return new Promise((resolve) => {
      setTimeout(() => {
        const { supported, name } = checkBrowser();
        if (supported) {
          setBrowser({ status: STATUS.PASS, detail: `${name} — supported ✓` });
        } else {
          setBrowser({ status: STATUS.FAIL, detail: `${name} — use Chrome or Edge` });
        }
        resolve(supported);
      }, 400);
    });
  }, []);

  /* ──── 2. Microphone check ──── */
  const runMicCheck = useCallback(() => {
    setMic({ status: STATUS.CHECKING, detail: "Requesting access…" });
    setMicLevel(0);
    return new Promise((resolve) => {
      const constraints = selectedMic
        ? { audio: { deviceId: { exact: selectedMic } } }
        : { audio: true };
      navigator.mediaDevices
        .getUserMedia(constraints)
        .then((stream) => {
          micStreamRef.current = stream;
          const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
          audioCtxRef.current = audioCtx;
          const source = audioCtx.createMediaStreamSource(stream);
          const analyser = audioCtx.createAnalyser();
          analyser.fftSize = 256;
          source.connect(analyser);
          analyserRef.current = analyser;
          const data = new Uint8Array(analyser.frequencyBinCount);

          let peakDetected = false;
          let elapsed = 0;
          const onTick = () => {
            analyser.getByteFrequencyData(data);
            const avg = data.reduce((a, b) => a + b, 0) / data.length;
            const level = Math.min(MIC_BARS, Math.round((avg / 128) * MIC_BARS));
            setMicLevel(level);
            if (avg > 10) peakDetected = true;
            elapsed += 200;
            if (elapsed >= 2500) {
              clearInterval(analyserTimerRef.current);
              enumerateDevices();
              setMic(peakDetected
                ? { status: STATUS.PASS, detail: "Microphone working ✓" }
                : { status: STATUS.PASS, detail: "Connected (try speaking)" });
              startLiveMicMonitor(selectedMic);
              resolve(true);
            }
          };
          analyserTimerRef.current = setInterval(onTick, 200);
        })
        .catch((err) => {
          const denied = err.name === "NotAllowedError";
          setMic({
            status: STATUS.FAIL,
            detail: denied ? "Access denied — allow in browser" : "No microphone found",
          });
          resolve(false);
        });
    });
  }, [selectedMic, enumerateDevices, startLiveMicMonitor]);

  /* ──── 3. Speaker check ──── */
  const playTestTone = useCallback(() => {
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      if (selectedSpeaker && ctx.setSinkId) {
        ctx.setSinkId(selectedSpeaker).catch(() => {});
      }
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "sine";
      osc.frequency.value = 440;
      gain.gain.value = 0.15;
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start();
      setTimeout(() => {
        osc.stop();
        ctx.close().catch(() => {});
      }, 800);
      return true;
    } catch {
      return false;
    }
  }, [selectedSpeaker]);

  const runSpeakerCheck = useCallback(() => {
    setSpeaker({ status: STATUS.CHECKING, detail: "Testing output…" });
    return new Promise((resolve) => {
      setTimeout(() => {
        const ok = playTestTone();
        if (ok) {
          setSpeaker({ status: STATUS.PASS, detail: "Speaker working ✓" });
        } else {
          setSpeaker({ status: STATUS.FAIL, detail: "Could not test speaker" });
        }
        resolve(ok);
      }, 100);
    });
  }, [playTestTone]);

  /* ──── 4. Internet speed check ──── */
  const runInternetCheck = useCallback(() => {
    setInternet({ status: STATUS.CHECKING, detail: "Measuring…", mbps: 0 });
    return measureSpeed().then((mbps) => {
      const good = mbps >= 5;
      setInternet({
        status: good ? STATUS.PASS : STATUS.FAIL,
        detail: good
          ? `${mbps} Mbps — good ✓`
          : `${mbps} Mbps — slow, switch network`,
        mbps,
      });
      return good;
    });
  }, []);

  /* ──── 5. Zoom host availability check (instructor + platformHosted only) ──── */
  const runZoomHostCheck = useCallback(async () => {
    if (role !== "instructor" || !batchId) {
      setZoomHost({ status: STATUS.PASS, detail: "Skipped" });
      return true;
    }
    setZoomHost({ status: STATUS.CHECKING, detail: "Checking classroom availability…" });
    setStaleHosts([]);
    try {
      const { data } = await apiClient.post(`/batches/${batchId}/pre-start-check`);
      if (data.available) {
        setZoomHost({ status: STATUS.PASS, detail: "Virtual classroom ready ✓" });
        return true;
      }
      if (data.staleHosts?.length > 0) {
        setStaleHosts(data.staleHosts);
        const names = data.staleHosts.map(h => h.batchName).join(", ");
        setZoomHost({
          status: STATUS.FAIL,
          detail: `Previous session not ended: ${names}`,
        });
        return false;
      }
      setZoomHost({ status: STATUS.FAIL, detail: data.message || "No classrooms available" });
      return false;
    } catch (err) {
      const msg = err?.response?.data?.message || "Could not verify classroom";
      // If non-platformHosted or 400 (already live, etc.), treat as not blocking
      if (err?.response?.status === 400) {
        setZoomHost({ status: STATUS.FAIL, detail: msg });
        return false;
      }
      // If the check API fails entirely, don't block — let startMeeting handle it
      setZoomHost({ status: STATUS.PASS, detail: "Check skipped — will verify on start" });
      return true;
    }
  }, [batchId, role]);

  /* ──── Force end stale session ──── */
  const handleForceEndStale = useCallback(async (staleBatchId) => {
    if (!batchId || !staleBatchId) return;
    setEndingStale(true);
    try {
      await apiClient.post(`/batches/${batchId}/force-end-stale`, { staleBatchId });
      setStaleHosts([]);
      // Re-run zoom check after ending stale
      await runZoomHostCheck();
    } catch (err) {
      const msg = err?.response?.data?.message || "Failed to end previous session";
      setZoomHost({ status: STATUS.FAIL, detail: msg });
    } finally {
      setEndingStale(false);
    }
  }, [batchId, runZoomHostCheck]);

  /* ──── Run all checks ──── */
  const runAllChecks = useCallback(async () => {
    setAllDone(false);
    setHasFailure(false);
    cleanup();

    const browserOk = await runBrowserCheck();
    const micOk = await runMicCheck();
    const speakerOk = await runSpeakerCheck();
    const internetOk = await runInternetCheck();
    const zoomOk = await runZoomHostCheck();

    setAllDone(true);
    setHasFailure(!browserOk || !micOk || !speakerOk || !internetOk || !zoomOk);
  }, [runBrowserCheck, runMicCheck, runSpeakerCheck, runInternetCheck, runZoomHostCheck, cleanup]);

  // Stable ref for useEffect
  const runAllChecksRef = useRef(runAllChecks);
  runAllChecksRef.current = runAllChecks;

  const handleMicChange = useCallback((deviceId) => {
    setSelectedMic(deviceId);
    if (micLiveActive) startLiveMicMonitor(deviceId);
  }, [micLiveActive, startLiveMicMonitor]);

  const handleSpeakerChange = useCallback((deviceId) => {
    setSelectedSpeaker(deviceId);
  }, []);

  const handleConfirm = () => {
    cleanup();
    onReady?.(sessionTopic.trim());
  };

  const handleClose = () => {
    cleanup();
    onCancel?.();
  };

  /* ──── Speed bar ──── */
  const speedPct = Math.min(100, (internet.mbps / 20) * 100);
  const roleLabel = role === "instructor" ? "Instructor" : "Student";
  const topicMissing = role === "instructor" && !sessionTopic.trim();

  if (!show) return null;

  return (
    <div className={OVERLAY} onClick={handleClose}>
      <div
        className={DIALOG}
        role="dialog"
        aria-modal="true"
        aria-label="System Check"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close button */}
        <button
          className="tw-absolute tw-right-3 tw-top-3 tw-z-[1] tw-flex tw-h-7 tw-w-7 tw-cursor-pointer tw-items-center tw-justify-center tw-rounded-[6px] tw-border-none tw-bg-transparent tw-text-content-muted tw-transition-colors tw-duration-150 hover:tw-bg-surface-sunken"
          onClick={handleClose}
          type="button"
          aria-label="Close"
        >
          <X size={16} />
        </button>

        {/* Header */}
        <div className="tw-flex tw-items-center tw-gap-3 tw-px-6 tw-pb-3 tw-pt-5">
          {/* The v1-blue gradient (#2563eb → #3b82f6) is the brand pair every other
              hero tile in the app already uses. */}
          <div className="tw-flex tw-h-10 tw-w-10 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-md tw-bg-gradient-to-br tw-from-primary-solid tw-to-chart-1 tw-text-primary-solid-fg">
            <Wifi size={22} />
          </div>
          <div>
            <h4 className="tw-m-0 tw-text-[1.1rem] tw-font-medium tw-leading-[1.3] tw-text-content">System Check</h4>
            <p className="tw-m-0 tw-text-[0.8rem] tw-text-content-muted">
              {batchName ? `${roleLabel} — ${batchName}` : "Checking your setup before joining"}
            </p>
          </div>
        </div>

        {/* Check items */}
        <div className="tw-flex tw-flex-col tw-gap-[0.6rem] tw-px-5 tw-pb-3 tw-pt-2">
          {/* Browser */}
          <div className={`${ITEM} ${ITEM_TONE[browser.status]}`}>
            <div className={`${PILL} ${PILL_TONE[browser.status]}`}>
              <Globe size={18} />
            </div>
            <div className="tw-min-w-0 tw-flex-1">
              <p className="tw-m-0 tw-text-[0.82rem] tw-font-semibold tw-text-content">Browser</p>
              <p className={DETAIL}>{browser.detail || "Waiting…"}</p>
            </div>
            <StatusIcon status={browser.status} />
          </div>

          {/* Microphone */}
          <div className={`${ITEM} ${ITEM_TONE[mic.status]}`}>
            <div className={`${PILL} ${PILL_TONE[mic.status]}`}>
              <Mic size={18} />
            </div>
            <div className="tw-min-w-0 tw-flex-1">
              <p className="tw-m-0 tw-text-[0.82rem] tw-font-semibold tw-text-content">Microphone</p>
              <p className={DETAIL}>{mic.detail || "Waiting…"}</p>
              {(mic.status === STATUS.CHECKING || micLiveActive) && <MicLevel level={micLevel} />}
              {microphones.length > 0 && (
                <div className="tw-relative tw-mt-[5px]">
                  <ChevronDown
                    size={12}
                    className="tw-pointer-events-none tw-absolute tw-right-[7px] tw-top-1/2 tw--translate-y-1/2 tw-text-content-subtle"
                  />
                  <select className={SELECT} value={selectedMic} onChange={(e) => handleMicChange(e.target.value)}>
                    {microphones.map((d) => (
                      <option key={d.deviceId} value={d.deviceId}>
                        {d.label || `Microphone ${microphones.indexOf(d) + 1}`}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>
            <StatusIcon status={mic.status} />
          </div>

          {/* Speaker */}
          <div className={`${ITEM} ${ITEM_TONE[speaker.status]}`}>
            <div className={`${PILL} ${PILL_TONE[speaker.status]}`}>
              <Volume2 size={18} />
            </div>
            <div className="tw-min-w-0 tw-flex-1">
              <p className="tw-m-0 tw-text-[0.82rem] tw-font-semibold tw-text-content">Speaker</p>
              <p className={DETAIL}>{speaker.detail || "Waiting…"}</p>
              {speakers.length > 0 && (
                <div className="tw-relative tw-mt-[5px]">
                  <ChevronDown
                    size={12}
                    className="tw-pointer-events-none tw-absolute tw-right-[7px] tw-top-1/2 tw--translate-y-1/2 tw-text-content-subtle"
                  />
                  <select className={SELECT} value={selectedSpeaker} onChange={(e) => handleSpeakerChange(e.target.value)}>
                    {speakers.map((d) => (
                      <option key={d.deviceId} value={d.deviceId}>
                        {d.label || `Speaker ${speakers.indexOf(d) + 1}`}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              {speaker.status === STATUS.PASS && (
                <button type="button" className={TEST_BTN} onClick={playTestTone}>
                  <Play size={11} /> Test Sound
                </button>
              )}
            </div>
            <StatusIcon status={speaker.status} />
          </div>

          {/* Internet Speed */}
          <div className={`${ITEM} ${ITEM_TONE[internet.status]}`}>
            <div className={`${PILL} ${PILL_TONE[internet.status]}`}>
              <Wifi size={18} />
            </div>
            <div className="tw-min-w-0 tw-flex-1">
              <p className="tw-m-0 tw-text-[0.82rem] tw-font-semibold tw-text-content">Internet Speed</p>
              <p
                className={`tw-m-0 tw-text-xs tw-leading-[1.4] ${
                  internet.status === STATUS.PASS
                    ? "tw-text-success"
                    : internet.status === STATUS.FAIL
                      ? "tw-text-danger"
                      : "tw-text-content-muted"
                }`}
              >
                {internet.detail || "Waiting…"}
              </p>
              {(internet.status === STATUS.PASS || internet.status === STATUS.FAIL) && (
                <div className={`tw-mt-1 tw-h-[5px] tw-w-full tw-overflow-hidden tw-rounded-[3px] ${TRACK}`}>
                  <div
                    className={`tw-h-full tw-rounded-[3px] tw-bg-gradient-to-r tw-transition-[width] tw-duration-[400ms] tw-ease-out ${
                      internet.mbps >= 5
                        ? "tw-from-success tw-to-[color-mix(in_srgb,hsl(var(--success))_82%,black)]"
                        : "tw-from-danger tw-to-[color-mix(in_srgb,hsl(var(--danger))_82%,black)]"
                    }`}
                    style={{ width: `${speedPct}%` }}
                  />
                </div>
              )}
            </div>
            <StatusIcon status={internet.status} />
          </div>

          {/* Zoom Host Availability (instructor only) */}
          {role === "instructor" && batchId && (
            <div className={`${ITEM} ${ITEM_TONE[zoomHost.status]}`}>
              <div className={`${PILL} ${PILL_TONE[zoomHost.status]}`}>
                <Monitor size={18} />
              </div>
              <div className="tw-min-w-0 tw-flex-1">
                <p className="tw-m-0 tw-text-[0.82rem] tw-font-semibold tw-text-content">Virtual Classroom</p>
                <p className={DETAIL}>{zoomHost.detail || "Waiting…"}</p>
                {staleHosts.length > 0 && (
                  <div className="tw-mt-1.5 tw-flex tw-flex-col tw-gap-1">
                    {staleHosts.map((sh) => (
                      <div key={sh.batchId} className={STALE_ROW}>
                        <span className="tw-truncate tw-font-medium tw-text-danger-fg">{sh.batchName}</span>
                        <button
                          type="button"
                          className={STALE_END_BTN}
                          disabled={endingStale}
                          onClick={() => handleForceEndStale(sh.batchId)}
                        >
                          {endingStale ? "Ending…" : "End & Proceed"}
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <StatusIcon status={zoomHost.status} />
            </div>
          )}

          {/* Session topic — instructor must fill before starting */}
          {role === "instructor" && (
            <div className="tw-mt-1 tw-border-0 tw-border-t tw-border-solid tw-border-line tw-pb-1 tw-pt-3.5">
              <label
                className="tw-mb-1.5 tw-block tw-text-[0.8125rem] tw-font-semibold tw-text-content"
                htmlFor="pmc-session-topic"
              >
                Today's Topic <span className="tw-ms-px tw-text-danger">*</span>
              </label>
              {/* ⚠️ The empty-state border was #fbbf24 in light and then overridden to
                  rgba(255,255,255,.15) in dark — the SAME value as the resting border,
                  so the "you must fill this in" cue did not exist in dark mode at all.
                  --warning is one token and shows in both. */}
              <input
                id="pmc-session-topic"
                type="text"
                className={`${TOPIC_INPUT} ${topicMissing ? "tw-border-warning" : "tw-border-line"}`}
                placeholder="e.g. Introduction to Arrays, Chapter 3 – Loops…"
                value={sessionTopic}
                onChange={(e) => setSessionTopic(e.target.value)}
                maxLength={160}
                autoComplete="off"
              />
              {topicMissing && (
                <p className="tw-mb-0 tw-mt-[0.3rem] tw-text-xs tw-text-content-muted">
                  Required — used to name the class recording automatically.
                </p>
              )}
            </div>
          )}
        </div>

        {/* Tip */}
        {hasFailure && allDone && (
          <div className={TIP}>
            <AlertTriangle size={14} />
            <span>Some checks failed. You can still join, but may experience issues.</span>
          </div>
        )}

        {/* Actions */}
        <div className="tw-mt-1 tw-flex tw-items-center tw-justify-end tw-gap-2 tw-border-0 tw-border-t tw-border-solid tw-border-line tw-px-5 tw-pb-5 tw-pt-3">
          <button className={BTN_GHOST} onClick={runAllChecks} disabled={!allDone} type="button">
            <RefreshCw size={13} /> Re-run
          </button>
          <button className={BTN_CANCEL} onClick={handleClose} type="button">
            Cancel
          </button>
          <button
            className={BTN_PRIMARY}
            onClick={handleConfirm}
            disabled={!allDone || topicMissing}
            type="button"
          >
            {!allDone ? "Checking…" : topicMissing ? "Enter Topic to Continue" : confirmText}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PreMeetingCheck;
